import { cn } from '../lib/cn';

import type { ResizableWidth } from '../lib/use-resizable-width';

/**
 * Drag handle for resizing a panel. Lives in the gutter between two surfaces: a faint grip
 * at rest so it can be found, stronger on hover, and the Mio colour while in use.
 * Also operable from the keyboard as an ARIA separator.
 */
export function ResizeHandle({
  resizable,
  label,
  className,
}: {
  resizable: ResizableWidth;
  label: string;
  className?: string;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={resizable.width}
      aria-valuemin={resizable.min}
      aria-valuemax={resizable.max}
      tabIndex={0}
      title={`${label}. Drag, or use arrow keys. Double-click to reset.`}
      className={cn(
        'group absolute inset-y-0 -left-3 z-20 hidden w-6 cursor-col-resize touch-none items-center justify-center outline-none lg:flex',
        className,
      )}
      {...resizable.handleProps}
    >
      <span
        className={cn(
          'w-[3px] rounded-full transition-all duration-150',
          resizable.dragging
            ? 'bg-mio h-24'
            : 'bg-line-strong group-hover:bg-fg-muted group-focus-visible:bg-mio h-9 group-hover:h-16 group-focus-visible:h-16',
        )}
        aria-hidden="true"
      />
    </div>
  );
}
